/**
 * ProjectSummary — the end of the room loop. Lists every room in the project
 * as the committed scope, asks two optional lead-scoring questions (budget and
 * timeline), then submits the whole project for a FREE estimate. There is no
 * payment — submitting flips the project to SUBMITTED server-side.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getProject,
  getIntakeOptions,
  getCustomerEstimate,
  submitProject,
  type ProjectView,
} from '../../lib/api';
import { ProjectMedia } from '../../components/ProjectMedia';
import type { IntakeOptions, CustomerEstimateItem } from '../../lib/types';

interface SummaryState {
  projectId?: string;
  /** Reopened a SUBMITTED/ACCEPTED project → read-only, no submit/add. */
  readOnly?: boolean;
}

export function ProjectSummary(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const summaryState = (location.state as SummaryState) ?? {};
  const projectId = summaryState.projectId;

  const [project, setProject] = useState<ProjectView | null>(null);
  const [options, setOptions] = useState<IntakeOptions | null>(null);
  // Per-room scope items (room id → the plain-language items its answers produced).
  const [scopeByRoom, setScopeByRoom] = useState<Record<string, CustomerEstimateItem[]>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [budgetRangeCode, setBudgetRangeCode] = useState<string | undefined>(undefined);
  const [timelineCode, setTimelineCode] = useState<string | undefined>(undefined);

  // Budget (Q13) → timeline (Q14) → review (full scope summary + Submit). The
  // scope summary is last, so it always comes after budget/timeline.
  const [step, setStep] = useState<'budget' | 'timeline' | 'review'>('budget');

  // Read-only when reopening an already-submitted project.
  const readOnly = summaryState.readOnly ?? false;

  useEffect(() => {
    if (!projectId) {
      setLoadError('We could not find your project.');
      return;
    }
    let active = true;
    Promise.all([getProject(projectId), getIntakeOptions()])
      .then(async ([p, opts]) => {
        if (!active) return;
        setProject(p);
        setOptions(opts);
        // Fetch each room's plain-language scope so the summary shows the answers,
        // not just the room label. Best-effort per room.
        const entries = await Promise.all(
          p.rooms.map(async (r) => {
            try {
              const est = await getCustomerEstimate(r.id);
              return [r.id, est.items] as const;
            } catch {
              return [r.id, [] as CustomerEstimateItem[]] as const;
            }
          }),
        );
        if (active) setScopeByRoom(Object.fromEntries(entries));
      })
      .catch(() => {
        if (active) setLoadError('We could not load your project just now.');
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  async function submit(): Promise<void> {
    if (!projectId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitProject(projectId, {
        ...(budgetRangeCode ? { budgetRangeCode } : {}),
        ...(timelineCode ? { timelineCode } : {}),
      });
      navigate('/design/submitted', { state: { projectId } });
    } catch {
      setSubmitError('We couldn\u2019t submit your project just now. Please try again.');
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="pb-note pb-note--flag" role="alert">
            {loadError}
          </div>
          <div className="q-actions">
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate('/service')}>
              Start over
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <p className="stage-card__lede">Putting your project together&hellip;</p>
        </div>
      </div>
    );
  }

  // The full-project scope block (rooms + their plain-language items). Shown on
  // the review step and in read-only.
  const scopeSummary = (
    <div>
      {project.rooms.map((r, i) => {
        const items = scopeByRoom[r.id] ?? [];
        return (
          <div
            key={r.id}
            style={{
              padding: 'var(--pb-3) 0',
              borderBottom:
                i < project.rooms.length - 1 ? 'var(--pb-border) solid var(--pb-rule-soft)' : 'none',
            }}
          >
            <div className="pb-option__label" style={{ marginBottom: 'var(--pb-2)' }}>
              {r.label ?? `Room ${i + 1}`}
            </div>
            {items.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 'var(--pb-4)' }}>
                {items.map((it, j) => (
                  <li key={j} style={{ fontSize: 'var(--pb-size-small)', padding: '2px 0' }}>
                    {it.name}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="estimate__hint">Scope saved.</span>
            )}
          </div>
        );
      })}
    </div>
  );

  // --- Read-only (reopened) -------------------------------------------------
  if (readOnly) {
    const sent = project.sentTotalCents;
    const isEstimated = sent != null;
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="progress__count">YOUR PROJECT</div>
          <h1>{project.name ?? 'Your project'}</h1>

          {isEstimated ? (
            <>
              {/* SENT: one total + a plain-language description of the work. */}
              <div className="estimate estimate--hero" style={{ marginBottom: 'var(--pb-3)' }}>
                <div className="estimate__label">YOUR ESTIMATE</div>
                <div className="estimate__value">
                  ${(sent / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="estimate__hint">Everything below is included.</div>
              </div>
              {scopeSummary}
            </>
          ) : (
            <>
              <div className="pb-note pb-note--good" role="status" style={{ marginBottom: 'var(--pb-3)' }}>
                {project.status === 'SUBMITTED'
                  ? 'Submitted \u2014 we\u2019ll have your estimate to you by the end of the next business day.'
                  : `Status: ${project.status}`}
              </div>
              {scopeSummary}
            </>
          )}

          {projectId ? <ProjectMedia projectId={projectId} /> : null}

          <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-4)' }}>
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate('/dashboard')}>
              Back to my projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Step 2: review the full project, then submit -------------------------
  if (step === 'review') {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="progress__count">REVIEW &amp; SUBMIT</div>
          <h1>{project.name ?? 'Here\u2019s the whole job'}</h1>
          <p className="stage-card__lede">
            Here&rsquo;s everything we&rsquo;ll quote. Submit it and we&rsquo;ll get your estimate back
            to you by the end of the next business day.
          </p>

          {scopeSummary}

          {projectId ? <ProjectMedia projectId={projectId} /> : null}

          {submitError ? (
            <div className="pb-note pb-note--flag" role="alert" style={{ marginTop: 'var(--pb-3)' }}>
              {submitError}
            </div>
          ) : null}

          <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-4)' }}>
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => void submit()} disabled={submitting}>
              {submitting ? 'Submitting\u2026' : 'Submit for your free estimate'}
            </button>
            <button
              type="button"
              className="pb-btn pb-btn--ghost"
              onClick={() => setStep('timeline')}
              disabled={submitting}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Step 1: budget + timeline (+ add another) ----------------------------
  // --- Q14: timeline ("When are you hoping to start?") ----------------------
  if (step === 'timeline') {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="progress__count">QUESTION 14</div>
          <h1>When are you hoping to start?</h1>
          {options ? (
            <div className="options" role="radiogroup" aria-label="Timeline" style={{ marginTop: 'var(--pb-3)' }}>
              {options.timelineOptions.map((opt) => {
                const isSel = opt.code === timelineCode;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    className="pb-option"
                    role="radio"
                    aria-checked={isSel}
                    onClick={() => setTimelineCode(isSel ? undefined : opt.code)}
                  >
                    <span
                      className="pb-option__mark"
                      style={isSel ? { background: 'var(--pb-chalk)', borderColor: 'var(--pb-chalk)', color: '#fff' } : undefined}
                    />
                    <span style={{ flexGrow: 1 }}>
                      <span className="pb-option__label">{opt.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-4)' }}>
            <button type="button" className="pb-btn pb-btn--lg pb-btn--next" onClick={() => setStep('review')}>
              Next
            </button>
            <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setStep('budget')}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Q13: budget ("What budget are you working with?") --------------------
  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <div className="progress__count">QUESTION 13</div>
        <h1>What budget are you working with?</h1>

        {options ? (
          <div className="options" role="radiogroup" aria-label="Budget range" style={{ marginTop: 'var(--pb-2)' }}>
            {options.budgetRanges.map((opt) => {
              const isSel = opt.code === budgetRangeCode;
              return (
                <button
                  key={opt.code}
                  type="button"
                  className="pb-option"
                  role="radio"
                  aria-checked={isSel}
                  onClick={() => setBudgetRangeCode(isSel ? undefined : opt.code)}
                >
                  <span
                    className="pb-option__mark"
                    style={isSel ? { background: 'var(--pb-chalk)', borderColor: 'var(--pb-chalk)', color: '#fff' } : undefined}
                  />
                  <span style={{ flexGrow: 1 }}>
                    <span className="pb-option__label">{opt.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-4)' }}>
          <button type="button" className="pb-btn pb-btn--lg pb-btn--next" onClick={() => setStep('timeline')}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
